import { AfterViewInit, Directive, ElementRef, NgZone, OnDestroy, Renderer2 } from '@angular/core';

/**
 * GUAI-2640 — Elementos flotantes fijos al viewport para tablas anchas y largas.
 *
 * Se aplica como atributo sobre un `<p-table>` (u otro elemento scrollable):
 *   <p-table appStickyHscroll ...>
 *
 * En estas tablas el wrapper de PrimeNG crece hasta contener todas las filas
 * (`height:auto`), así que quien hace scroll vertical es la ventana, no el
 * wrapper. Eso deja dos elementos fuera de alcance:
 *
 *  1) La barra de scroll horizontal real queda al fondo del wrapper, por debajo
 *     del viewport → se muestra una BARRA flotante fija abajo.
 *  2) La cabecera (`thead`, sticky respecto al wrapper) se va por arriba al bajar
 *     por las filas → se muestra una CABECERA flotante fija arriba.
 *
 * Ambos elementos flotantes se sincronizan con el scroll horizontal de la tabla. Además, mientras la tabla
 * está desplazada a la derecha, la raíz `.p-datatable` (y su réplica flotante) llevan la clase `is-hscrolled`.
 * La lógica de DOM/listeners está encapsulada aquí; no ensucia el componente.
 */
@Directive({
    selector: '[appStickyHscroll]',
    standalone: false
})
export class StickyHscrollDirective implements AfterViewInit, OnDestroy {

  private scroller?: HTMLElement;   // elemento que scrollea (.p-datatable-wrapper)
  private table?: HTMLElement;      // <table> interno
  private thead?: HTMLElement;      // cabecera real (.p-datatable-thead)

  private bar?: HTMLElement;        // barra horizontal flotante (abajo)
  private spacer?: HTMLElement;     // relleno que fija el ancho scrollable de la barra
  private headerHost?: HTMLElement; // contenedor flotante fijo de la cabecera (arriba)
  private headerTable?: HTMLElement; // <table> clon que aloja el thead clonado
  private root?: HTMLElement;        // `.p-datatable` raíz de la tabla real
  private headerRoot?: HTMLElement;  // réplica del `.p-datatable` raíz en la cabecera flotante

  private ro?: ResizeObserver;
  private mo?: MutationObserver;
  private rafId: number | null = null;
  private syncing = false;
  private headerDirty = true;       // el clon de la cabecera necesita rehacerse

  constructor(
    private host: ElementRef<HTMLElement>,
    private renderer: Renderer2,
    private zone: NgZone
  ) { }

  ngAfterViewInit(): void {
    setTimeout(() => this.init(), 0); // esperar a que PrimeNG pinte el wrapper
  }

  private init(): void {
    this.scroller = this.resolveScroller();
    if (!this.scroller) return;
    this.table = this.scroller.querySelector<HTMLElement>('table') ?? undefined;
    this.thead = this.table?.querySelector<HTMLElement>('thead') ?? undefined;
    this.root = (this.scroller.closest('.p-datatable') as HTMLElement | null) ?? undefined;

    this.buildBar();
    this.buildHeader();

    this.zone.runOutsideAngular(() => {
      this.scroller!.addEventListener('scroll', this.onScrollerScroll, { passive: true });
      window.addEventListener('scroll', this.onWindowChange, { passive: true });
      window.addEventListener('resize', this.onWindowChange);
    });

    // Reaccionar a cambios de tamaño/contenido de la tabla (carga de filas, etc.).
    this.ro = new ResizeObserver(() => this.zone.runOutsideAngular(() => { this.syncHeaderWidths(); this.update(); }));
    this.ro.observe(this.scroller);
    const inner = this.scroller.firstElementChild as HTMLElement | null;
    if (inner) this.ro.observe(inner);

    // Reflejar en el clon los cambios de la cabecera real (iconos/estado de orden).
    if (this.thead) {
      this.mo = new MutationObserver(() => this.zone.runOutsideAngular(() => { this.headerDirty = true; this.onWindowChange(); }));
      this.mo.observe(this.thead, { subtree: true, childList: true, attributes: true, characterData: true });
    }

    this.update();
  }

  private resolveScroller(): HTMLElement | undefined {
    const el = this.host.nativeElement;
    return el.classList.contains('p-datatable-wrapper') ? el : (el.querySelector<HTMLElement>('.p-datatable-wrapper') ?? undefined);
  }

  // ── Barra horizontal flotante (abajo) ────────────────────────────────────

  private buildBar(): void {
    this.bar = this.renderer.createElement('div');
    this.spacer = this.renderer.createElement('div');
    this.renderer.setAttribute(this.bar, 'style',
      'position:fixed;bottom:0;height:14px;overflow-x:auto;overflow-y:hidden;z-index:1000;display:none;background:rgba(255,255,255,.85)');
    this.renderer.setAttribute(this.spacer, 'style', 'height:1px');
    this.renderer.appendChild(this.bar, this.spacer);
    this.renderer.appendChild(document.body, this.bar);
    this.zone.runOutsideAngular(() => this.bar!.addEventListener('scroll', this.onBarScroll, { passive: true }));
  }

  // ── Cabecera flotante (arriba) ───────────────────────────────────────────

  private buildHeader(): void {
    if (!this.thead || !this.table) return;
    const root = this.root;

    this.headerHost = this.renderer.createElement('div');
    this.renderer.setAttribute(this.headerHost, 'style',
      'position:fixed;top:0;overflow:hidden;z-index:999;display:none;box-shadow:0 2px 4px rgba(0,0,0,.12)'); // z-index<1000 (banner)

    // Réplica del `.p-datatable` raíz: sin ella las celdas pierden fondo/borde/padding.
    // `margin/padding:0!important` anula las utilidades de espaciado (mt-4, …) de la raíz.
    const inner = this.renderer.createElement('div');
    this.renderer.setAttribute(inner, 'class', root?.className ?? 'p-datatable');
    this.renderer.setAttribute(inner, 'style', 'display:block;margin:0!important;padding:0!important');
    this.headerRoot = inner;

    // Tabla clon: reutiliza las clases de PrimeNG para heredar estilos.
    this.headerTable = this.renderer.createElement('table');
    this.renderer.setAttribute(this.headerTable, 'class', this.table.className);
    this.renderer.setAttribute(this.headerTable, 'style', 'table-layout:fixed;margin:0');

    this.renderer.appendChild(inner, this.headerTable);
    this.renderer.appendChild(this.headerHost, inner);
    this.renderer.appendChild(document.body, this.headerHost);

    this.cloneThead();
    this.zone.runOutsideAngular(() => this.headerHost!.addEventListener('click', this.onHeaderClick));
  }

  private cloneThead(): void {
    if (!this.headerTable || !this.thead) return;
    this.headerTable.innerHTML = '';
    this.headerTable.appendChild(this.thead.cloneNode(true));
    this.headerDirty = false;
    this.syncHeaderWidths();
  }

  // Copia los anchos de columna reales al clon (con table-layout:fixed basta `width`).
  private syncHeaderWidths(): void {
    if (!this.headerTable || !this.thead || !this.table) return;
    const real = this.thead.querySelectorAll<HTMLElement>('tr > th');
    const clone = this.headerTable.querySelectorAll<HTMLElement>('tr > th');
    if (real.length !== clone.length) return;
    this.renderer.setStyle(this.headerTable, 'width', this.table.getBoundingClientRect().width + 'px');
    real.forEach((th, i) => this.renderer.setStyle(clone[i], 'width', th.getBoundingClientRect().width + 'px'));
  }

  // Clic en el clon → clic en la columna real equivalente (dispara la ordenación).
  private onHeaderClick = (e: Event): void => {
    const cloneTh = (e.target as HTMLElement).closest('th');
    if (!cloneTh || !this.headerTable || !this.thead) return;
    const i = Array.from(this.headerTable.querySelectorAll('tr > th')).indexOf(cloneTh);
    if (i >= 0) this.thead.querySelectorAll<HTMLElement>('tr > th')[i]?.click();
  };

  // ── Scroll horizontal: tabla ⇄ barra, tabla → cabecera ───────────────────

  private onScrollerScroll = (): void => {
    if (!this.scroller) return;
    this.markHScrolled();
    if (this.headerTable) this.renderer.setStyle(this.headerTable, 'margin-left', -this.scroller.scrollLeft + 'px');
    if (this.syncing || !this.bar) return;
    this.syncing = true; this.bar.scrollLeft = this.scroller.scrollLeft; this.syncing = false;
  };

  // `is-hscrolled` en la raíz (y en su réplica de la cabecera flotante) mientras la tabla está desplazada a la
  // derecha: permite pintar lo que solo tiene sentido con contenido pasando por debajo, como la sombra de las
  // columnas fijas.
  private markHScrolled(): void {
    const scrolled = (this.scroller?.scrollLeft ?? 0) > 0;
    [this.root, this.headerRoot].forEach(el => {
      if (!el) return;
      if (scrolled) this.renderer.addClass(el, 'is-hscrolled');
      else this.renderer.removeClass(el, 'is-hscrolled');
    });
  }

  private onBarScroll = (): void => {
    if (this.syncing || !this.bar || !this.scroller) return;
    this.syncing = true; this.scroller.scrollLeft = this.bar.scrollLeft; this.syncing = false;
  };

  private onWindowChange = (): void => {
    if (this.rafId != null) return;
    this.rafId = requestAnimationFrame(() => { this.rafId = null; this.update(); });
  };

  private update(): void {
    if (!this.scroller) return;
    const rect = this.scroller.getBoundingClientRect(); // una sola medición para ambos
    this.updateBar(rect);
    this.updateHeader(rect);
  }

  // Barra abajo: visible si la tabla desborda a lo ancho y su barra real (al fondo) no se ve.
  private updateBar(rect: DOMRect): void {
    if (!this.bar || !this.spacer) return;
    const s = this.scroller!;
    const vh = window.innerHeight;
    const show = s.scrollWidth > s.clientWidth + 1 && rect.bottom > vh && rect.top < vh && rect.bottom > 0;
    this.renderer.setStyle(this.bar, 'display', show ? 'block' : 'none');
    if (!show) return;
    this.renderer.setStyle(this.bar, 'left', rect.left + 'px');
    this.renderer.setStyle(this.bar, 'width', s.clientWidth + 'px');
    this.renderer.setStyle(this.spacer, 'width', s.scrollWidth + 'px');
    this.bar.scrollLeft = s.scrollLeft;
  }

  // Cabecera arriba: visible cuando la real se salió por encima del offset y la tabla sigue en pantalla.
  private updateHeader(rect: DOMRect): void {
    if (!this.headerHost || !this.headerTable || !this.thead) return;
    const top = this.topOffset();
    const show = rect.top < top && rect.bottom > top + this.thead.offsetHeight;
    this.renderer.setStyle(this.headerHost, 'display', show ? 'block' : 'none');
    if (!show) return;
    if (this.headerDirty) this.cloneThead(); // reclonar solo si cambió el orden o es la primera vez
    this.renderer.setStyle(this.headerHost, 'top', top + 'px');
    this.renderer.setStyle(this.headerHost, 'left', rect.left + 'px');
    this.renderer.setStyle(this.headerHost, 'width', this.scroller!.clientWidth + 'px');
    this.renderer.setStyle(this.headerTable, 'margin-left', -this.scroller!.scrollLeft + 'px');
  }

  // Borde inferior del cromo fijo superior para anclar la cabecera flotante debajo:
  // el topbar sticky (su bottom ya incluye el banner) o, si no hay, el banner de pruebas.
  private topOffset(): number {
    const topbar = document.querySelector('app-topbar')?.getBoundingClientRect();
    if (topbar && topbar.height > 0) return topbar.bottom;
    const banner = document.querySelector('.top-banner-danger')?.getBoundingClientRect();
    return banner && banner.height > 0 ? banner.bottom : 0;
  }

  ngOnDestroy(): void {
    this.scroller?.removeEventListener('scroll', this.onScrollerScroll);
    this.bar?.removeEventListener('scroll', this.onBarScroll);
    this.headerHost?.removeEventListener('click', this.onHeaderClick);
    window.removeEventListener('scroll', this.onWindowChange);
    window.removeEventListener('resize', this.onWindowChange);
    this.ro?.disconnect();
    this.mo?.disconnect();
    if (this.rafId != null) cancelAnimationFrame(this.rafId);
    this.bar?.remove();
    this.headerHost?.remove();
  }
}
